import React, { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles, CheckCircle2, Circle, FileSignature, FileText, Wrench, ClipboardList, GraduationCap, Coffee, ChevronRight, Building2, Award, ExternalLink } from "lucide-react";

const iconFor = (cat, type) => {
  if (type === "diagram") return Building2;
  if (type === "quiz") return Award;
  if (type === "training") return GraduationCap;
  return ({
    Compliance: FileSignature,
    Legal: FileText,
    Payroll: ClipboardList,
    IT: Wrench,
    Culture: Coffee,
    Training: GraduationCap,
    Onboarding: Sparkles,
  }[cat] || Circle);
};

const isAutoTask = (type) => ["diagram", "quiz", "training"].includes(type);

export default function Onboarding() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/onboarding/my-tasks");
    setTasks(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    try {
      await api.post(`/onboarding/tasks/${id}/complete`, {});
      await load();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };

  const required = tasks.filter((x) => x.required);
  const optional = tasks.filter((x) => !x.required);
  const requiredDone = required.filter((x) => x.status === "completed").length;
  const pct = required.length === 0 ? 100 : Math.round((requiredDone / required.length) * 100);

  if (loading) return <div className="text-[#747474]">{t("common.loading")}</div>;
  if (tasks.length === 0) {
    return (
      <div className="sw-card text-center py-16">
        <CheckCircle2 size={40} className="text-[#008932] mx-auto mb-3" />
        <h2 className="sw-heading text-[22px] mb-2">{t("onb.no_pending")}</h2>
        <p className="text-[14px] text-[#747474]">{t("onb.no_tasks")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Welcome hero */}
      <div className="relative rounded-[28px] overflow-hidden p-8 lg:p-10 text-white" style={{ background: "linear-gradient(135deg, #1e293b 0%, #475569 50%, #334155 100%)" }}>
        <div className="absolute inset-0 sw-grain opacity-60" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-3 py-1 text-[11px] uppercase tracking-wider mb-3">
            <Sparkles size={12} /> Welcome aboard
          </div>
          <h1 className="sw-heading text-white text-[28px] md:text-[40px] leading-tight">
            Welcome home, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-white/85 mt-2 text-[15px] max-w-2xl">
            We're so happy you joined the Sagewood family. Complete the steps below to finish your onboarding.
          </p>
          <div className="mt-6 max-w-md">
            <div className="flex items-center justify-between text-[12px] text-white/80 mb-2">
              <span>Progress</span>
              <span>{requiredDone}/{required.length} required completed</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#e2e8f0] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Required */}
      <div>
        <h2 className="sw-heading text-[18px] mb-3">{t("onb.required_steps")}</h2>
        <div className="flex flex-col gap-3">
          {required.map((task) => {
            const Icon = iconFor(task.category, task.type);
            const done = task.status === "completed";
            const auto = isAutoTask(task.type);
            return (
              <div key={task.id} className={`sw-card flex items-center gap-4 ${done ? "opacity-70" : ""}`}>
                <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0 ${done ? "bg-[#b8e6cd] text-[#0b430b]" : "bg-[#e2e8f0] text-[#475569]"}`}>
                  {done ? <CheckCircle2 size={20} /> : <Icon size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-[15px] ${done ? "line-through text-[#747474]" : "text-[#323232]"}`}>{task.title}</div>
                  <div className="text-[11px] text-[#747474] uppercase tracking-wider mt-0.5">
                    {task.category} · {task.type}
                    {auto && !done && <span className="ml-2 text-[#475569] normal-case tracking-normal italic">{t("onb.auto_completes")}</span>}
                  </div>
                </div>
                {!done && auto && task.completion_url && (
                  <Link data-testid={`open-task-${task.id}`} to={task.completion_url} className="sw-btn-primary !px-4 !py-2 !text-[12px]">
                    {t("common.open")} <ExternalLink size={12} />
                  </Link>
                )}
                {!done && !auto && (
                  <button data-testid={`complete-task-${task.id}`} onClick={() => complete(task.id)} className="sw-btn-primary !px-4 !py-2 !text-[12px]">
                    {t("onb.mark_done")} <ChevronRight size={12} />
                  </button>
                )}
                {done && <span className="sw-badge sw-badge-approved">{t("common.done")}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Optional */}
      {optional.length > 0 && (
        <div>
          <h2 className="sw-heading text-[18px] mb-3">{t("onb.optional")}</h2>
          <div className="flex flex-col gap-3">
            {optional.map((task) => {
              const Icon = iconFor(task.category, task.type);
              const done = task.status === "completed";
              const auto = isAutoTask(task.type);
              return (
                <div key={task.id} className={`sw-card flex items-center gap-4 ${done ? "opacity-70" : ""}`}>
                  <div className={`w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0 ${done ? "bg-[#b8e6cd] text-[#0b430b]" : "bg-[#f6f6f6] text-[#747474]"}`}>
                    {done ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                  </div>
                  <div className="flex-1">
                    <div className={`text-[14px] ${done ? "line-through text-[#747474]" : "text-[#323232]"}`}>{task.title}</div>
                    <div className="text-[11px] text-[#747474] uppercase tracking-wider mt-0.5">{task.category}</div>
                  </div>
                  {!done && auto && task.completion_url && (
                    <Link to={task.completion_url} className="sw-btn-secondary !px-3 !py-1.5 !text-[12px]">{t("common.open")} <ExternalLink size={11} /></Link>
                  )}
                  {!done && !auto && (
                    <button onClick={() => complete(task.id)} className="sw-btn-secondary !px-3 !py-1.5 !text-[12px]">{t("onb.mark_done")}</button>
                  )}
                  {done && <span className="sw-badge sw-badge-approved">{t("common.done")}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
